/**
 * "Continue with Google" — the client half of the PKCE code flow (#80a).
 *
 * The server exchanges the code at POST /auth/google (redirect_uri is pinned
 * server-side to `${appOrigin}/auth/google/callback`). The client id is
 * build-time config (VITE_GOOGLE_CLIENT_ID); while unset the button reports
 * the honest ©auth.oauth_fail instead of pretending (copy-tone §1.5).
 */

const VERIFIER_KEY = "humble.google_pkce_verifier";

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function googleClientId(): string | null {
  const id = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  return id && id.length > 0 ? id : null;
}

/** Start the redirect round-trip. Returns false when Google isn't configured. */
export async function startGoogleSignIn(): Promise<boolean> {
  const clientId = googleClientId();
  if (!clientId) return false;
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(48)));
  try {
    sessionStorage.setItem(VERIFIER_KEY, verifier);
  } catch {
    return false; // no storage ⇒ the callback could never finish the exchange
  }
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = b64url(new Uint8Array(digest));
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: `${window.location.origin}/auth/google/callback`,
    response_type: "code",
    scope: "openid email profile",
    code_challenge: challenge,
    code_challenge_method: "S256",
    prompt: "select_account",
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
  return true;
}

/** Consume (once) the verifier stashed by startGoogleSignIn. */
export function takeGoogleVerifier(): string | null {
  try {
    const v = sessionStorage.getItem(VERIFIER_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    return v;
  } catch {
    return null;
  }
}
